<script lang="ts">
	import dark from 'stores/theme';
	import userStore from 'stores/user';
	import { onDestroy, onMount } from 'svelte';
	import { get } from 'svelte/store';
	import { goto } from '$app/navigation';
	import CloseMenuIcon from 'assets/svg/closeMenu.svg?component';
	import activeCurrency from 'stores/activeCurrency';
	import CurrencySelector from 'components/Header/CurrencySelector/CurrencySelector.svelte';
	import LocaleLink from 'components/LocaleLink/LocaleLink.svelte';
	import Dollar from 'assets/svg/dollarIcon.svg?url';
	import Bitcoin from 'assets/svg/bitcoin.svg?url';
	import Euro from 'assets/svg/euroIcon.svg?url';
	import Ethereum from 'assets/svg/ethereum.svg?url';
	import UserProfile from 'assets/svg/userProfile.svg?component';
	import LightTheme from 'assets/svg/lightTheme.svg?component';
	import DarkTheme from 'assets/svg/darkTheme.svg?component';
	import Home from 'assets/svg/home.svg?component';
	import Portfolio from 'assets/svg/portfolio.svg?component';
	import Star from 'assets/svg/star.svg?component';
	import Exchanges from 'assets/svg/exchanges.svg?component';
	import DotsIcon from 'assets/svg/dots.svg?component';
	import activeLocale from 'stores/activeLocale';
	import { _ } from 'utils/i18/i18n';
	import Learn from 'assets/svg/learn.svg?component';
	import News from 'assets/svg/news.svg?component';
	import Glossary from 'assets/svg/glossary.svg?component';

	$: normalizedLocale = $activeLocale.locale === 'en' ? '/' : `/${$activeLocale.locale}/`;

	export let darkTheme = false;

	let isMobileMenuOpen = false;
	let menuRef;

	const currencies = [
		{ icon: Dollar, value: 'Dollar', currency: 'usd' },
		{ icon: Bitcoin, value: 'Bitcoin', currency: 'btc' },
		{ icon: Euro, value: 'Euro', currency: 'eur' },
		{ icon: Ethereum, value: 'Ethereum', currency: 'eth' },
	];

	let selectedCurrency = currencies[0];

	onMount(() => {
		const activeCur = get(activeCurrency);
		getInitData(activeCur);
		document.addEventListener('click', handleClose);

		return () => {
			document.removeEventListener('click', handleClose);
		};
	});

	let Modal;
	let isModalActive = false;
	function toggleModal() {
		import('components/ModalAsyncSignInUpForgot/ModalAsyncSignInUpForgot.svelte').then(res => Modal = res.default)
		isModalActive = !isModalActive;
	}

	const handleClose = (e: Event) => {
		if (menuRef && !menuRef.contains(e.target)) {
			isMobileMenuOpen = false;
		}
	};

	const getInitData = (inputData: { currency: string; price: number }) => {
		selectedCurrency = currencies.filter((x) => inputData.currency === x.currency)[0];
	};
</script>

<div class="menu-mobile-wrapper" bind:this={menuRef} class:dark={darkTheme}>
	<div class="burger" on:click={() => (isMobileMenuOpen = !isMobileMenuOpen)}>
		{#if !isMobileMenuOpen}
			<DotsIcon class="burger-icon" />
		{:else}
			<CloseMenuIcon class="burger-icon" />
		{/if}
	</div>
	<div class="menu-mobile" class:visible={isMobileMenuOpen}>
		<ul
			class="mobile-nav-list"
			on:click={() => {
				isMobileMenuOpen = false;
			}}
		>
			<li
				class="mobile-nav-item"
				on:click={() => {
					dark.set(!$dark);
				}}
			>
				{#if darkTheme}
					<p class="mobile-nav-link">
						<LightTheme width={14} height={14} class="link-icon" />
						<span class="link-text">{$_('navigation.lightMode')}</span>
					</p>
				{:else}
					<p class="mobile-nav-link">
						<DarkTheme width={14} height={14} class="link-icon" />
						<span class="link-text">{$_('navigation.darkMode')}</span>
					</p>
				{/if}
			</li>
			<li
				class="mobile-nav-item"
				on:click={() => {
					if (!$userStore.isLoggedIn) toggleModal();
				}}
			>
				{#if $userStore.isLoggedIn}
					<p
						class="mobile-nav-link"
						on:click={() => {
							goto(`/account${normalizedLocale}`);
						}}
					>
						<UserProfile width={14} height={14} class="link-icon" />
						<span class="link-text">{$_('navigation.myAccount')}</span>
					</p>
				{:else}
					<p class="mobile-nav-link">
						<UserProfile width={14} height={14} class="link-icon" />
						<span class="link-text">{$_('navigation.signIn')}</span>
					</p>
				{/if}
			</li>
			<li class="mobile-nav-item">
				<LocaleLink href="/">
					<span class="mobile-nav-link">
						<Home width={14} height={14} class="link-icon" />
						<span class="link-text">{$_('navigation.main')}</span>
					</span>
				</LocaleLink>
			</li>
			<li class="mobile-nav-item">
				<LocaleLink href="/exchanges">
					<span class="mobile-nav-link">
						<Exchanges width={14} height={14} class="link-icon" />
						<span class="link-text">{$_('navigation.exchanges')}</span>
					</span>
				</LocaleLink>
			</li>
			<li class="mobile-nav-item">
				<div
					class="mobile-nav-link"
					on:click={() => {
						if (!$userStore.isLoggedIn) toggleModal();
						else goto(`/watchlist${normalizedLocale}`);
					}}
				>
					<Star width={14} height={14} class="link-icon" />
					<span class="link-text">{$_('navigation.watchlist')}</span>
				</div>
			</li>
			<li class="mobile-nav-item">
				<div
					class="mobile-nav-link"
					on:click={() => {
						if (!$userStore.isLoggedIn) toggleModal();
						else goto(`/portfolio${normalizedLocale}`);
					}}
				>
					<Portfolio width={14} height={14} class="link-icon" />
					<span class="link-text">{$_('navigation.portfolio')}</span>
				</div>
			</li>
			<li class="mobile-nav-item">
				<div
					class="mobile-nav-link"
					on:click={() => {
						goto(`/learn${normalizedLocale}`);
					}}
				>
					<Learn width={14} height={14} class="link-icon" />
					<span class="link-text">{$_('navigation.learn')}</span>
				</div>
			</li>
			<li class="mobile-nav-item">
				<div
					class="mobile-nav-link"
					on:click={() => {
						goto(`/news${normalizedLocale}`);
					}}
				>
					<News width={14} height={14} class="link-icon" />
					<span class="link-text">{$_('navigation.news')}</span>
				</div>
			</li>
			<li class="mobile-nav-item">
				<div
					class="mobile-nav-link"
					on:click={() => {
						goto(`/learn/glossary${normalizedLocale}`);
					}}
				>
					<Glossary width={14} height={14} class="link-icon" />
					<span class="link-text">{$_('navigation.glossary')}</span>
				</div>
			</li>
			<li class="mobile-nav-item dropdown" on:click|stopPropagation={() => {}}>
				<!-- <LocaleSelector dark={darkTheme} /> -->
			</li>
			<li class="mobile-nav-item dropdown" on:click|stopPropagation={() => {}}>
				<CurrencySelector dark={darkTheme} isMobileMenu={true}/>
			</li>
		</ul>
	</div>
</div>

<svelte:component this="{Modal}" bind:active={isModalActive} {toggleModal} dark={darkTheme}/>

<style lang="scss">
	@import 'mobileMenu';
</style>
